import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import {
  readMarketplaceCartItems,
  writeMarketplaceCartItems,
} from "./marketplaceCartStore";
import { EmptyState, PageHeader, ToneBadge } from "./petaarUi";

const currency = (value) => `₹${Number(value || 0).toFixed(2)}`;

const MARKETPLACE_SECTIONS = [
  { key: "food", title: "Food essentials" },
  { key: "lifestyle", title: "Lifestyle & accessories" },
  { key: "healthcare", title: "Healthcare" },
  { key: "pet_parent", title: "Pet parent" },
];

const DEMO_MARKETPLACE_ITEMS = [
  { id: "demo-1", title: "Dry food", category: "Food", sectionKey: "food", imageUrl: "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=640&q=80&auto=format&fit=crop", price: 799, badge: "TRENDING", isDemo: true },
  { id: "demo-2", title: "Wet food", category: "Food", sectionKey: "food", imageUrl: "https://images.unsplash.com/photo-1583512603805-3cc6b41f3edb?w=640&q=80&auto=format&fit=crop", price: 599, isDemo: true },
  { id: "demo-3", title: "Treats", category: "Treats", sectionKey: "food", imageUrl: "https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=640&q=80&auto=format&fit=crop", price: 299, isDemo: true },
  { id: "demo-4", title: "Prescription food", category: "Health Supplements", sectionKey: "food", imageUrl: "https://images.unsplash.com/photo-1596854407944-bf87f6fdd49e?w=640&q=80&auto=format&fit=crop", price: 1099, isDemo: true },
  { id: "demo-5", title: "Toys", category: "Toys", sectionKey: "lifestyle", imageUrl: "https://images.unsplash.com/photo-1522276498395-f4f68f7f8454?w=640&q=80&auto=format&fit=crop", price: 399, isDemo: true },
  { id: "demo-6", title: "Walk essentials", category: "Accessories", sectionKey: "lifestyle", imageUrl: "https://images.unsplash.com/photo-1548767797-d8c844163c4c?w=640&q=80&auto=format&fit=crop", price: 699, isDemo: true },
  { id: "demo-7", title: "Grooming tools", category: "Grooming", sectionKey: "lifestyle", imageUrl: "https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?w=640&q=80&auto=format&fit=crop", price: 499, isDemo: true },
  { id: "demo-8", title: "Beds & mats", category: "Accessories", sectionKey: "lifestyle", imageUrl: "https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?w=640&q=80&auto=format&fit=crop", price: 1299, isDemo: true },
  { id: "demo-9", title: "Skin care", category: "Medicines", sectionKey: "healthcare", imageUrl: "https://images.unsplash.com/photo-1585435557343-3b092031a831?w=640&q=80&auto=format&fit=crop", price: 349, isDemo: true },
  { id: "demo-10", title: "Dewormer", category: "Medicines", sectionKey: "healthcare", imageUrl: "https://images.unsplash.com/photo-1474511320723-9a56873867b5?w=640&q=80&auto=format&fit=crop", price: 249, isDemo: true },
  { id: "demo-11", title: "Joint care", category: "Health Supplements", sectionKey: "healthcare", imageUrl: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=640&q=80&auto=format&fit=crop", price: 699, isDemo: true },
  { id: "demo-12", title: "Cardiac care", category: "Medicines", sectionKey: "healthcare", imageUrl: "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=640&q=80&auto=format&fit=crop", price: 899, badge: "SELLING FAST", isDemo: true },
  { id: "demo-13", title: "Fashion", category: "Accessories", sectionKey: "pet_parent", imageUrl: "https://images.unsplash.com/photo-1525253086316-d0c936c814f8?w=640&q=80&auto=format&fit=crop", price: 549, isDemo: true },
  { id: "demo-14", title: "Key chains", category: "Accessories", sectionKey: "pet_parent", imageUrl: "https://images.unsplash.com/photo-1517849845537-4d257902454a?w=640&q=80&auto=format&fit=crop", price: 199, isDemo: true },
  { id: "demo-15", title: "Mugs", category: "Accessories", sectionKey: "pet_parent", imageUrl: "https://images.unsplash.com/photo-1525253013412-55c1a69a5738?w=640&q=80&auto=format&fit=crop", price: 299, isDemo: true },
  { id: "demo-16", title: "Stationary", category: "Accessories", sectionKey: "pet_parent", imageUrl: "https://images.unsplash.com/photo-1516214104703-d870798883c5?w=640&q=80&auto=format&fit=crop", price: 249, isDemo: true },
];

const toSectionKey = (productCategory = "") => {
  const category = String(productCategory || "").trim().toLowerCase();
  if (["food", "treats"].includes(category)) return "food";
  if (["medicines", "health supplements"].includes(category)) return "healthcare";
  if (["toys", "accessories", "grooming"].includes(category)) return "lifestyle";
  return "pet_parent";
};

const demoSectionKey = (item) => item.sectionKey || toSectionKey(item.category);

const getProductImages = (product) => {
  const gallery = Array.isArray(product?.imageUrls)
    ? product.imageUrls.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  if (gallery.length > 0) return gallery;
  const fallback = String(product?.imageUrl || "").trim();
  return fallback ? [fallback] : [];
};

const buildVariantOptions = (price) => {
  const base = Math.max(1, Number(price) || 1);
  return [
    { label: "1x Pack", qty: 1, price: Math.round(base), mrp: Math.round(base * 1.18), unit: "1 pack" },
    { label: "2x Pack", qty: 2, price: Math.round(base * 2), mrp: Math.round(base * 2.2), unit: "2 pack" },
    { label: "3x Pack", qty: 3, price: Math.round(base * 3), mrp: Math.round(base * 3.3), unit: "3 pack" },
    { label: "5x Pack", qty: 5, price: Math.round(base * 5), mrp: Math.round(base * 5.5), unit: "5 pack" },
  ];
};

export default function OwnerMarketplace({ user, onOpenCartPage }) {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [cartItems, setCartItems] = useState([]);

  const loadProducts = useCallback(async () => {
    try {
      setLoadingProducts(true);
      setLoadError("");
      const snapshot = await getDocs(
        query(collection(db, "marketplaceProducts"), where("isActive", "==", true)),
      );
      const rows = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter((item) => item.isActive === true);
      rows.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setProducts(rows);
    } catch (error) {
      setLoadError(error.message || "Unable to load marketplace products.");
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    if (!user?.uid) {
      setOrders([]);
      return;
    }
    try {
      setLoadingOrders(true);
      const byUid = await getDocs(query(collection(db, "marketplaceOrders"), where("buyerUid", "==", user.uid)));
      const rows = byUid.docs.map((item) => ({ id: item.id, ...item.data() }));
      rows.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setOrders(rows);
    } catch (error) {
      setLoadError(error.message || "Unable to load marketplace orders.");
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    setCartItems(readMarketplaceCartItems());
  }, []);

  useEffect(() => {
    writeMarketplaceCartItems(cartItems);
  }, [cartItems]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const categories = useMemo(() => {
    const source = [...products, ...DEMO_MARKETPLACE_ITEMS];
    return [...new Set(source.map((item) => String(item.category || "").trim()).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b),
    );
  }, [products]);

  const filteredProducts = useMemo(() => {
    const queryText = searchText.trim().toLowerCase();
    return products.filter((item) => {
      if (categoryFilter && item.category !== categoryFilter) return false;
      if (!queryText) return true;
      return (
        String(item.title || "").toLowerCase().includes(queryText) ||
        String(item.category || "").toLowerCase().includes(queryText) ||
        String(item.description || "").toLowerCase().includes(queryText)
      );
    });
  }, [products, searchText, categoryFilter]);

  const filteredDemoProducts = useMemo(() => {
    const queryText = searchText.trim().toLowerCase();
    return DEMO_MARKETPLACE_ITEMS.filter((item) => {
      if (categoryFilter && item.category !== categoryFilter) return false;
      if (!queryText) return true;
      return (
        String(item.title || "").toLowerCase().includes(queryText) ||
        String(item.category || "").toLowerCase().includes(queryText)
      );
    });
  }, [searchText, categoryFilter]);

  const previewSections = useMemo(
    () =>
      MARKETPLACE_SECTIONS.map((section) => ({
        ...section,
        products: DEMO_MARKETPLACE_ITEMS.filter((item) => demoSectionKey(item) === section.key).slice(0, 8),
      })),
    [],
  );

  const showingDemo = filteredProducts.length === 0 && filteredDemoProducts.length > 0;
  const displayProducts = showingDemo ? filteredDemoProducts : filteredProducts;

  const sectionedProducts = useMemo(() => {
    const bucket = new Map(MARKETPLACE_SECTIONS.map((section) => [section.key, []]));
    displayProducts.forEach((product) => {
      const sectionKey = product.isDemo ? demoSectionKey(product) : toSectionKey(product.category);
      const rows = bucket.get(sectionKey);
      if (!rows) return;
      rows.push(product);
    });
    return MARKETPLACE_SECTIONS
      .map((section) => ({
        ...section,
        products: bucket.get(section.key) || [],
      }))
      .filter((section) => section.products.length > 0);
  }, [displayProducts]);

  const cartCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + Math.max(1, Number(item.qty) || 1), 0),
    [cartItems],
  );

  const selectedProductImages = useMemo(
    () => getProductImages(selectedProduct),
    [selectedProduct],
  );

  const selectedProductVariants = useMemo(
    () => buildVariantOptions(selectedProduct?.price),
    [selectedProduct?.price],
  );

  const addToCart = (product, quantity = 1) => {
    if (!product?.id || product?.isDemo) {
      alert("This is a preview item. Live provider products will be added to cart.");
      return;
    }
    const qty = Math.max(1, Number(quantity) || 1);
    setCartItems((prev) => {
      const idx = prev.findIndex((item) => item.productId === product.id);
      if (idx === -1) {
        return [
          ...prev,
          {
            productId: product.id,
            title: String(product.title || "Product"),
            category: String(product.category || ""),
            imageUrl: Array.isArray(product.imageUrls)
              ? String(product.imageUrls[0] || "")
              : String(product.imageUrl || ""),
            price: Number(product.price || 0),
            qty,
            providerId: String(product.providerId || ""),
            providerEmail: String(product.providerEmail || ""),
          },
        ];
      }
      const copy = [...prev];
      copy[idx] = { ...copy[idx], qty: Math.max(1, Number(copy[idx].qty || 1) + qty) };
      return copy;
    });
    alert("Added to cart.");
  };

  const openProductDetail = (product) => {
    if (!product) return;
    setSelectedProduct(product);
    setSelectedImageIndex(0);
    setSelectedVariantIndex(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="Marketplace"
        title="A curated pet marketplace"
        subtitle="Shop pet products from verified Petaar partners, explore categories, and keep your cart ready for checkout."
        actions={(
          <button className="petaar-button" onClick={() => onOpenCartPage?.()}>
            Cart ({cartCount})
          </button>
        )}
      />
      {loadError && (
        <div className="petaar-notice error" style={{ marginBottom: 16 }}>
          {loadError}
        </div>
      )}

      <div className="petaar-card" style={{ marginBottom: 18 }}>
        <div className="petaar-form-grid" style={{ marginBottom: 16 }}>
        <input
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search toys, food, medicine, accessories..."
        />
        <select
          value={categoryFilter}
          onChange={(event) => setCategoryFilter(event.target.value)}
        >
          <option value="">All categories</option>
          {categories.map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
        <button
          className="petaar-button--secondary"
          onClick={() => {
            setSearchText("");
            setCategoryFilter("");
            setSelectedProduct(null);
          }}
        >
          Reset
        </button>
        <button
          className="petaar-button"
          onClick={() => onOpenCartPage?.()}
        >
          Cart ({cartCount})
        </button>
        </div>
        <div className="petaar-chip-row">
          {categories.slice(0, 12).map((item) => {
            const active = categoryFilter === item;
            return (
              <button
                key={`market-category-${item}`}
                className={active ? "petaar-button" : "petaar-button--secondary"}
                onClick={() => setCategoryFilter(active ? "" : item)}
                style={{ minHeight: 36, padding: "8px 14px", fontSize: 13 }}
              >
                {item}
              </button>
            );
          })}
        </div>
      </div>

      <section
        className="petaar-card"
        style={{ marginBottom: 18 }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>Browse Categories</h3>
        <div style={{ display: "grid", gap: 18 }}>
          {previewSections.map((section) => (
            <div key={`preview-${section.key}`}>
              <h2 style={{ margin: "0 0 10px", fontSize: 20, lineHeight: 1.2 }}>{section.title}</h2>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {section.products.map((item) => (
                  <button
                    key={`preview-item-${item.id}`}
                    onClick={() => {
                      setCategoryFilter(String(item.category || ""));
                      setSearchText("");
                      setSelectedProduct(null);
                    }}
                    style={{
                      border: "1px solid #e2e8f0",
                      background: "#fff",
                      borderRadius: 12,
                      width: 132,
                      padding: 8,
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                    title={`Show ${item.category} products`}
                  >
                    <div
                      style={{
                        height: 86,
                        borderRadius: 10,
                        border: "1px solid #e5e7eb",
                        background: "#f3f4f6",
                        display: "grid",
                        placeItems: "center",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        style={{ maxWidth: "92%", maxHeight: "92%", objectFit: "contain" }}
                      />
                    </div>
                    <div style={{ marginTop: 8, fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>{item.title}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {selectedProduct ? (
        <section
          className="petaar-card"
          style={{ marginBottom: 18 }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 24 }}>{selectedProduct.title || "Product"}</h3>
            <button onClick={() => setSelectedProduct(null)} style={{ border: "1px solid #cbd5e1", background: "#fff", borderRadius: 8, padding: "8px 12px", fontWeight: 700 }}>
              Close
            </button>
          </div>
          <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "70px minmax(0,1fr)" }}>
              <div style={{ display: "grid", gap: 8, alignContent: "start" }}>
                {selectedProductImages.map((imageUrl, index) => (
                  <button
                    key={`${selectedProduct.id}-thumb-${index}`}
                    onClick={() => setSelectedImageIndex(index)}
                    style={{
                      border: index === selectedImageIndex ? "2px solid #f97316" : "1px solid #cbd5e1",
                      borderRadius: 10,
                      padding: 4,
                      background: "#fff",
                      height: 66,
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <img src={imageUrl} alt={`thumb-${index + 1}`} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                  </button>
                ))}
              </div>
              <div style={{ border: "1px solid #dbe3ee", borderRadius: 12, background: "#f8fafc", minHeight: 420, display: "grid", placeItems: "center", padding: 12, position: "relative" }}>
                {selectedProductImages[selectedImageIndex] ? (
                  <img
                    src={selectedProductImages[selectedImageIndex]}
                    alt={selectedProduct.title || "Product"}
                    style={{ maxWidth: "100%", maxHeight: 392, objectFit: "contain" }}
                  />
                ) : (
                  <div style={{ color: "#64748b", fontSize: 13 }}>No image</div>
                )}
                <span style={{ position: "absolute", right: 12, top: 12, padding: "4px 8px", borderRadius: 999, background: "#ecfdf5", color: "#166534", border: "1px solid #86efac", fontWeight: 800, fontSize: 12 }}>
                  CERTIFIED
                </span>
              </div>
            </div>
            <div style={{ display: "grid", gap: 10, alignContent: "start" }}>
              <div style={{ border: "1px solid #bbf7d0", background: "#f0fdf4", borderRadius: 10, padding: "10px 12px", display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <strong style={{ color: "#15803d" }}>Lightning Deal</strong>
                <span style={{ color: "#475569", fontWeight: 700 }}>Ends in 22h 9m</span>
              </div>
              <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(2,minmax(0,1fr))" }}>
                {selectedProductVariants.map((option, index) => {
                  const saved = Math.max(0, Number(option.mrp || 0) - Number(option.price || 0));
                  const percent = option.mrp > 0 ? Math.round((saved / option.mrp) * 100) : 0;
                  return (
                    <button
                      key={`${selectedProduct.id}-variant-${option.label}`}
                      onClick={() => setSelectedVariantIndex(index)}
                      style={{
                        textAlign: "left",
                        border: index === selectedVariantIndex ? "2px solid #f97316" : "1px solid #cbd5e1",
                        background: "#fff",
                        borderRadius: 10,
                        padding: "10px 11px",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ fontWeight: 700, color: "#475569" }}>{option.label}</div>
                      <div style={{ marginTop: 6, fontWeight: 900, fontSize: 30, lineHeight: 1 }}>{currency(option.price)}</div>
                      <div style={{ marginTop: 3, color: "#64748b" }}>
                        MRP <span style={{ textDecoration: "line-through" }}>{currency(option.mrp)}</span>
                      </div>
                      <div style={{ marginTop: 2, color: "#15803d", fontWeight: 800 }}>{percent}% OFF</div>
                    </button>
                  );
                })}
              </div>
              <div>
                <strong style={{ display: "block", marginBottom: 2 }}>Check delivery date</strong>
                <div style={{ color: "#64748b", fontSize: 14 }}>24 hours delivery available</div>
                <div style={{ marginTop: 8, borderBottom: "1px solid #dbe3ee", paddingBottom: 8, display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ color: "#94a3b8" }}>Check delivery date</span>
                  <button style={{ border: "none", background: "transparent", color: "#f97316", fontWeight: 800, cursor: "pointer" }}>Check</button>
                </div>
              </div>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <strong>Bank offers and coupons</strong>
                <button style={{ border: "none", background: "transparent", color: "#f97316", fontWeight: 800, cursor: "pointer" }}>Check offers</button>
              </div>
              <div style={{ display: "grid", gap: 4, color: "#475569" }}>
                <div>COD available for orders between ₹700-₹5000</div>
                <div>Free delivery on orders above ₹599</div>
              </div>
              <button
                onClick={() => addToCart(selectedProduct, selectedProductVariants[selectedVariantIndex]?.qty || 1)}
                style={{
                  border: "1px solid #f97316",
                  background: "#f97316",
                  color: "#fff",
                  borderRadius: 12,
                  padding: "11px 14px",
                  fontWeight: 800,
                  fontSize: 18,
                }}
              >
                Add to Cart
              </button>
              <div style={{ border: "1px solid #dbe3ee", borderRadius: 10, background: "#fff", padding: "10px 12px" }}>
                <h4 style={{ margin: "0 0 8px" }}>Product Details</h4>
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <strong>Suitable for</strong>
                    <span>Dogs/Cats aged 2+ months</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <strong>Category</strong>
                    <span>{selectedProduct.category || "General"}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <strong>Pack type</strong>
                    <span>{selectedProductVariants[selectedVariantIndex]?.unit || "1 pack"}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <strong>Key benefits</strong>
                    <span>Healthy daily nutrition & better coat health</span>
                  </div>
                </div>
                <p style={{ marginTop: 10, marginBottom: 0, color: "#334155", lineHeight: 1.5 }}>
                  {String(selectedProduct.description || "No description provided by seller.")}
                </p>
              </div>
              <button
                onClick={() => onOpenCartPage?.()}
                style={{
                  border: "1px solid #1d4ed8",
                  background: "#1d4ed8",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontWeight: 800,
                }}
              >
                Go to cart
              </button>
            </div>
          </div>
        </section>
      ) : null}

      <div className="petaar-section-title">
        <div>
          <div className="petaar-kicker">Products</div>
          <h2 style={{ marginTop: 4 }}>Shop by category</h2>
        </div>
      </div>
      {loadingProducts ? (
        <div className="petaar-card">Loading products...</div>
      ) : displayProducts.length === 0 ? (
        <EmptyState
          icon="🛒"
          title="No products listed yet"
          description="Marketplace products will appear here as partner stores publish inventory."
        />
      ) : (
        <div style={{ display: "grid", gap: 22 }}>
          {showingDemo ? (
            <div className="petaar-notice info">
              Preview mode: live marketplace items are not listed yet by providers. Showing sample layout.
            </div>
          ) : null}
          {sectionedProducts.map((section) => (
            <section key={section.key}>
              <h2 style={{ margin: "0 0 12px", fontSize: 24, lineHeight: 1.1 }}>{section.title}</h2>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                {section.products.map((product) => {
                  const primaryImage = Array.isArray(product.imageUrls)
                    ? String(product.imageUrls[0] || "")
                    : String(product.imageUrl || "");
                  const badgeText = String(product.badge || "").trim();
                  return (
                    <article
                      key={product.id}
                      style={{
                        width: 244,
                        maxWidth: "100%",
                        borderRadius: 14,
                        border: "1px solid #dbe3ee",
                        background: "#fff",
                        overflow: "hidden",
                        boxShadow: "0 10px 24px rgba(15,23,42,0.05)",
                      }}
                    >
                      <div
                        onClick={() => openProductDetail(product)}
                        style={{
                          width: "calc(100% - 20px)",
                          margin: 10,
                          height: 182,
                          borderRadius: 12,
                          background: "#f3f4f6",
                          border: "1px solid #e5e7eb",
                          display: "grid",
                          placeItems: "center",
                          overflow: "hidden",
                          position: "relative",
                          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.6)",
                          cursor: "pointer",
                        }}
                      >
                        {primaryImage ? (
                          <img
                            src={primaryImage}
                            alt={product.title || "Product"}
                            style={{
                              width: "92%",
                              height: "92%",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <div style={{ color: "#64748b", fontSize: 13, padding: 8 }}>No image</div>
                        )}
                        {badgeText ? (
                          <span
                            style={{
                              position: "absolute",
                              left: 8,
                              bottom: 8,
                              padding: "4px 10px",
                              borderRadius: 999,
                              background: "#a3e635",
                              color: "#1f2937",
                              fontWeight: 800,
                              fontSize: 11,
                            }}
                          >
                            {badgeText}
                          </span>
                        ) : null}
                      </div>
                      <div style={{ padding: "0 12px 12px", textAlign: "left" }}>
                        <button
                          onClick={() => openProductDetail(product)}
                          style={{
                            fontSize: 16,
                            lineHeight: 1.2,
                            fontWeight: 800,
                            color: "#111827",
                            minHeight: 39,
                            border: "none",
                            background: "transparent",
                            padding: 0,
                            textAlign: "left",
                            cursor: "pointer",
                          }}
                        >
                          {product.title || "Product"}
                        </button>
                        <div style={{ marginTop: 4, fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                          {product.category || "General"}
                        </div>
                        <div style={{ marginTop: 3, fontSize: 25, lineHeight: 1, fontWeight: 900, color: "#111827" }}>
                          {currency(product.price)}
                        </div>
                        <button
                          onClick={() => {
                            addToCart(product);
                          }}
                          title={product.isDemo ? `${product.title} (preview)` : `Add ${product.title} to cart`}
                          style={{
                            marginTop: 10,
                            width: "100%",
                            border: "1px solid #f97316",
                            background: "#fff7ed",
                            color: "#ea580c",
                            borderRadius: 10,
                            padding: "9px 10px",
                            fontWeight: 800,
                            cursor: "pointer",
                          }}
                        >
                          Add to cart
                        </button>
                        <button
                          onClick={() => openProductDetail(product)}
                          style={{
                            marginTop: 8,
                            width: "100%",
                            border: "1px solid #cbd5e1",
                            background: "#fff",
                            color: "#0f172a",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontWeight: 700,
                          }}
                        >
                          View details
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {user && (
        <>
          <div className="petaar-section-title" style={{ marginTop: 24 }}>
            <div>
              <div className="petaar-kicker">Orders</div>
              <h2 style={{ marginTop: 4 }}>Your marketplace purchases</h2>
            </div>
          </div>
          {loadingOrders ? (
            <div className="petaar-card">Loading orders...</div>
          ) : orders.length === 0 ? (
            <EmptyState
              icon="📦"
              title="No product orders yet"
              description="Once you place a marketplace order, shipment and payment details will show up here."
            />
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {orders.map((order) => {
                return (
                  <div key={order.id} className="petaar-card">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <strong>{order.productTitle || "Product"}</strong>
                      <ToneBadge tone={String(order.status || "").toLowerCase() === "delivered" ? "success" : String(order.status || "").toLowerCase() === "cancelled" ? "danger" : "warning"}>
                        {order.status || "placed"}
                      </ToneBadge>
                    </div>
                    <p style={{ margin: "6px 0", color: "#334155" }}>
                      Qty: {order.quantity || 1} | Total: <strong>{currency(order.totalAmount)}</strong> | Payment: {order.paymentMode || "cod"}
                    </p>
                    <p style={{ margin: "6px 0", color: "#64748b" }}>
                      Ship to: {order.shippingAddress || "N/A"}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
